// Formula parser – extracts cell/range references from Excel formula strings.
function stripDollars(ref) {
    return ref.replace(/\$/g, "");
}
// Replace matches with same-length blanks so later regexes keep original positions.
function blankMatches(text, re) {
    return text.replace(re, (match) => " ".repeat(match.length));
}
// A cell ref, cell range, full-column range, or full-row range (the part after "!").
const REF_PART = "\\$?[A-Z]{1,3}\\$?\\d{1,7}(?::\\$?[A-Z]{1,3}\\$?\\d{1,7})?" +
    "|\\$?[A-Z]{1,3}:\\$?[A-Z]{1,3}" +
    "|\\$?\\d{1,7}:\\$?\\d{1,7}";
// Sheet-name prefix: quoted (with '' as an escaped apostrophe) or unquoted.
// Unquoted names may contain periods (e.g. =Rev.2024!A1).
const SHEET_PREFIX = "(?:'((?:''|[^'])+)'|([A-Za-z0-9_.]+))";
// External-workbook references ([Book.xlsx]Sheet1!A1, '[Book.xlsx]My Sheet'!A1,
// 'C:\path\[Book.xlsx]Sheet1'!A1). We cannot read other workbooks, so these are
// removed before parsing rather than mis-read as local sheet references.
const externalRefRe = new RegExp(`(?:'[^']*\\[[^']*\\][^']*'|\\[[^\\]]+\\][A-Za-z0-9_.]+)!(?:${REF_PART})`, "g");
// 3-D references (Sheet1:Sheet3!A1, 'Sheet 1:Sheet 3'!A1). Sheet names cannot
// contain ":", so a colon before "!" always means a 3-D span. Not supported;
// removed so the last sheet isn't mis-read as the only precedent.
const threeDRefRe = new RegExp(`(?:'[^']*:[^']*'|[A-Za-z0-9_.]+:[A-Za-z0-9_.]+)!(?:${REF_PART})`, "g");
const crossSheetRe = new RegExp(`${SHEET_PREFIX}!\\$?([A-Z]{1,3})\\$?(\\d{1,7})(?::\\$?([A-Z]{1,3})\\$?(\\d{1,7}))?`, "g");
const crossSheetRowRe = new RegExp(`${SHEET_PREFIX}!\\$?(\\d{1,7}):\\$?(\\d{1,7})`, "g");
const crossSheetColRe = new RegExp(`${SHEET_PREFIX}!\\$?([A-Z]{1,3}):\\$?([A-Z]{1,3})`, "g");
const sameSheetRe = /(?<![A-Za-z0-9_!])(\$?[A-Z]{1,3}\$?\d{1,7})(?::(\$?[A-Z]{1,3}\$?\d{1,7}))?(?![A-Za-z0-9_(])/g;
const sameSheetColRe = /(?<![A-Za-z0-9_!:$])(\$?[A-Z]{1,3}):(\$?[A-Z]{1,3})(?![A-Za-z0-9_(:])/g;
const sameSheetRowRe = /(?<![A-Za-z0-9_!:$.\d])(\$?\d{1,7}):(\$?\d{1,7})(?![A-Za-z0-9_(:.])/g;
function unescapeSheetName(quoted, unquoted) {
    return quoted != null ? quoted.replace(/''/g, "'") : unquoted;
}
export function parseFormulaPrecedents(formulaStr, originSheet) {
    if (!formulaStr || !formulaStr.startsWith("="))
        return [];
    // Blank (not delete) string literals, external refs, and 3-D refs so every
    // later match keeps its position in the original formula.
    let cleaned = blankMatches(formulaStr.slice(1), /"[^"]*"/g);
    cleaned = blankMatches(cleaned, externalRefRe);
    cleaned = blankMatches(cleaned, threeDRefRe);
    const collected = [];
    let m;
    while ((m = crossSheetRe.exec(cleaned)) !== null) {
        const sheetName = unescapeSheetName(m[1], m[2]);
        const startCell = stripDollars(m[3] + m[4]);
        const isRange = !!(m[5] && m[6]);
        const endCell = isRange ? stripDollars(m[5] + m[6]) : null;
        const ref = isRange ? `${startCell}:${endCell}` : startCell;
        collected.push({ pos: m.index, ref, sheet: sheetName, display: `${sheetName}!${ref}`, isRange });
    }
    while ((m = crossSheetRowRe.exec(cleaned)) !== null) {
        const sheetName = unescapeSheetName(m[1], m[2]);
        const ref = `${stripDollars(m[3])}:${stripDollars(m[4])}`;
        collected.push({ pos: m.index, ref, sheet: sheetName, display: `${sheetName}!${ref}`, isRange: true });
    }
    while ((m = crossSheetColRe.exec(cleaned)) !== null) {
        const sheetName = unescapeSheetName(m[1], m[2]);
        const ref = `${stripDollars(m[3])}:${stripDollars(m[4])}`;
        collected.push({ pos: m.index, ref, sheet: sheetName, display: `${sheetName}!${ref}`, isRange: true });
    }
    let withoutCrossSheet = blankMatches(cleaned, crossSheetRe);
    withoutCrossSheet = blankMatches(withoutCrossSheet, crossSheetRowRe);
    withoutCrossSheet = blankMatches(withoutCrossSheet, crossSheetColRe);
    while ((m = sameSheetRe.exec(withoutCrossSheet)) !== null) {
        const startCell = stripDollars(m[1]);
        const isRange = !!m[2];
        const endCell = isRange ? stripDollars(m[2]) : null;
        const ref = isRange ? `${startCell}:${endCell}` : startCell;
        collected.push({ pos: m.index, ref, sheet: originSheet, display: ref, isRange });
    }
    while ((m = sameSheetColRe.exec(withoutCrossSheet)) !== null) {
        const ref = `${stripDollars(m[1])}:${stripDollars(m[2])}`;
        collected.push({ pos: m.index, ref, sheet: originSheet, display: ref, isRange: true });
    }
    while ((m = sameSheetRowRe.exec(withoutCrossSheet)) !== null) {
        const ref = `${stripDollars(m[1])}:${stripDollars(m[2])}`;
        collected.push({ pos: m.index, ref, sheet: originSheet, display: ref, isRange: true });
    }
    collected.sort((a, b) => a.pos - b.pos);
    const occurrenceCounts = {};
    const results = [];
    for (const item of collected) {
        const key = `${item.sheet}!${item.ref}`.toUpperCase();
        const occ = occurrenceCounts[key] || 0;
        occurrenceCounts[key] = occ + 1;
        results.push({
            ref: item.ref,
            sheet: item.sheet,
            display: item.display,
            isRange: item.isRange,
            occurrence: occ,
        });
    }
    return results;
}
